import { z } from 'zod';

import { env } from '@/core/env/env';

/**
 * Auth configuration fetched from `/v1/identity/config`. The bearer token is the access_token,
 * but the session profile is still mapped from id_token claims, so the schema enforces `openid`.
 */
export interface AuthConfig {
  poolId: string;
  clientId: string;
  domain: string;
  scopes: string[];
  /** Absent when the backend sends none or an invalid one: the idle timeout is then off. */
  idleTimeoutSeconds?: number;
}

const authConfigSchema = z
  .object({
    user_pool_id: z.string().regex(/^[a-z]{2}-[a-z]+-\d_\w+$/),
    client_id: z.string().min(1),
    oauth_domain: z.string().url(),
    scopes: z.array(z.string()),
    // `.catch`, not a hard failure: a bad value turns the idle timeout off instead of failing the
    // whole parse, since login working matters more than this defense-in-depth control.
    idle_timeout_seconds: z.number().int().positive().optional().catch(undefined),
  })
  .refine((data) => data.scopes.includes('openid'), {
    message: 'scopes must include "openid" — the profile claims require an id_token',
    path: ['scopes'],
  });

/**
 * Fetches auth configuration from the backend. Uses plain `fetch` rather than the API client:
 * interceptors depend on `getUserManager()`, which is only available after this completes.
 */
export async function fetchAuthConfig(): Promise<AuthConfig> {
  const base = env.VITE_API_BASE_URL.replace(/\/$/, '');
  const url = new URL(`${base}/v1/identity/config`);
  const response = await fetch(url, { signal: AbortSignal.timeout(10_000) });

  if (!response.ok) {
    throw new Error(`Auth config request failed: ${response.status} ${response.statusText}`);
  }

  const data = authConfigSchema.parse(await response.json());

  return {
    poolId: data.user_pool_id,
    clientId: data.client_id,
    domain: new URL(data.oauth_domain).host,
    scopes: data.scopes,
    idleTimeoutSeconds: data.idle_timeout_seconds,
  };
}
